import { io, Socket } from "socket.io-client";

// Maintain a register of event handlers to reapply after reconnections
type EventHandler = {
  event: string;
  callback: (...args: any[]) => void;
};

class SocketService {
  private socket: WebSocket | null = null;
  private reconnectInterval: number = 5000; // 5 sec
  private pingInterval: NodeJS.Timeout | null = null;
  private isManuallyClosed: boolean = false;

  private static instance: SocketService;
  protectedSocket: Socket | null = null;
  openSocket: Socket | null = null;

  // Track registered event handlers to reapply after reconnections
  private protectedEventHandlers: EventHandler[] = [];
  private openEventHandlers: EventHandler[] = [];

  // Track current connection parameters to enable auto-reconnect
  private protectedParams: Record<string, any> | null = null;
  private openParams: Record<string, any> | null = null;

  // Track connection state
  private isProtectedConnecting: boolean = false;
  private isOpenConnecting: boolean = false;

  // Private constructor to prevent direct instantiation
  private constructor() {
    // Add global window event listener for offline/online events
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleOnline.bind(this));
      window.addEventListener("focus", this.handleFocus.bind(this));
    }
  }

  private handleOnline() {
    console.log("Network connection restored, reconnecting sockets...");
    // Reconnect both sockets with their last known parameters
    if (this.protectedParams) {
      this.connect(this.protectedParams, true);
    }
    if (this.openParams) {
      this.connect(this.openParams, false);
    }
  }

  private handleFocus() {
    // When tab becomes active again, check connection state
    if (
      this.protectedSocket &&
      !this.protectedSocket.connected &&
      this.protectedParams
    ) {
      console.log("Tab focused, reconnecting protected socket...");
      this.connect(this.protectedParams, true);
    }
    if (this.openSocket && !this.openSocket.connected && this.openParams) {
      console.log("Tab focused, reconnecting open socket...");
      this.connect(this.openParams, false);
    }
  }

  // Singleton instance getter
  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  // Connect method to initiate the WebSocket connection
  connect(
    params: Record<string, any> = {},
    isProtectedRoute: boolean
  ): Promise<boolean> {
    return new Promise((resolve) => {
      // Store params for reconnection
      if (isProtectedRoute) {
        this.protectedParams = { ...params };
      } else {
        this.openParams = { ...params };
      }

      // Define the WebSocket URL
      const URL = "wss://ws.tylt.money";
      console.log("WS URL: ", URL);

      // Add query parameters to the URL
      const queryString = new URLSearchParams(params).toString();
      let fullUrl = isProtectedRoute
        ? `${URL}/analyticsDashboardPanel?${queryString}`
        : `${URL}/open?${queryString}`;

      // For protected routes with token
      if (isProtectedRoute && params.token) {
        // If already connecting, avoid duplicate connection attempts
        if (this.isProtectedConnecting) {
          console.log("Protected socket connection already in progress...");
          return resolve(false);
        }

        // If already connected and functional, just resolve
        if (this.protectedSocket && this.protectedSocket.connected) {
          console.log("Protected socket already connected.");
          return resolve(true);
        }

        // If there's an existing socket but disconnected, clean it up
        if (this.protectedSocket) {
          this.protectedSocket.removeAllListeners();
          this.protectedSocket.disconnect();
          this.protectedSocket = null;
        }

        this.isProtectedConnecting = true;

        // Create new socket connection
        this.protectedSocket = io(fullUrl, {
          reconnection: true,
          reconnectionAttempts: Infinity, // Keep trying to reconnect
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          timeout: 20000,
          transports: ["websocket"],
          autoConnect: true,
        });

        // Handle connection success
        this.protectedSocket.on("connect", () => {
          console.log("Connected to the protected webSocket!");
          this.isProtectedConnecting = false;

          // Reapply all registered event handlers
          this.reapplyEventHandlers(true);

          resolve(true);
        });

        // Handle reconnection attempts
        this.protectedSocket.on("reconnect_attempt", (attempt) => {
          console.log(`Protected socket reconnection attempt #${attempt}`);
        });

        // Handle successful reconnection
        this.protectedSocket.on("reconnect", (attempt) => {
          console.log(
            `Protected socket reconnected successfully after ${attempt} attempts!`
          );

          // Reapply all registered event handlers after reconnection
          this.reapplyEventHandlers(true);
        });

        // Handle reconnection error
        this.protectedSocket.on("reconnect_error", (error) => {
          console.error("Protected socket reconnection failed:", error);
        });

        // Handle disconnection
        this.protectedSocket.on("disconnect", (reason) => {
          console.warn(`Protected socket disconnected: ${reason}`);

          // If disconnect reason is transport close or ping timeout, attempt reconnect
          if (
            reason === "transport close" ||
            reason === "ping timeout" ||
            reason === "transport error"
          ) {
            setTimeout(() => {
              if (this.protectedParams) {
                console.log(
                  "Attempting to reconnect protected socket after disconnect..."
                );
                this.connect(this.protectedParams, true);
              }
            }, 3000);
          }
        });

        // Handle connection errors
        this.protectedSocket.on("connect_error", (err) => {
          console.error("Protected WebSocket connection error:", err);
          this.isProtectedConnecting = false;
          resolve(false);

          // Retry connection after a delay
          setTimeout(() => {
            if (this.protectedParams) {
              console.log(
                "Retrying protected socket connection after error..."
              );
              this.connect(this.protectedParams, true);
            }
          }, 5000);
        });
      }
      // For open routes
      else if (!isProtectedRoute) {
        // If already connecting, avoid duplicate connection attempts
        if (this.isOpenConnecting) {
          console.log("Open socket connection already in progress...");
          return resolve(false);
        }

        // If already connected and functional, just resolve
        if (this.openSocket && this.openSocket.connected) {
          console.log("Open socket already connected.");
          return resolve(true);
        }

        // If there's an existing socket but disconnected, clean it up
        if (this.openSocket) {
          this.openSocket.removeAllListeners();
          this.openSocket.disconnect();
          this.openSocket = null;
        }

        this.isOpenConnecting = true;

        // Create new socket connection
        this.openSocket = io(fullUrl, {
          reconnection: true,
          reconnectionAttempts: Infinity, // Keep trying to reconnect
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          timeout: 20000,
          transports: ["websocket"],
          autoConnect: true,
        });

        // Handle connection success
        this.openSocket.on("connect", () => {
          console.log("Connected to the open webSocket!");
          this.isOpenConnecting = false;

          // Reapply all registered event handlers
          this.reapplyEventHandlers(false);

          resolve(true);
        });

        // Handle reconnection attempts
        this.openSocket.on("reconnect_attempt", (attempt) => {
          console.log(`Open socket reconnection attempt #${attempt}`);
        });

        // Handle successful reconnection
        this.openSocket.on("reconnect", (attempt) => {
          console.log(
            `Open socket reconnected successfully after ${attempt} attempts!`
          );

          // Reapply all registered event handlers after reconnection
          this.reapplyEventHandlers(false);
        });

        // Handle reconnection error
        this.openSocket.on("reconnect_error", (error) => {
          console.error("Open socket reconnection failed:", error);
        });

        // Handle disconnection
        this.openSocket.on("disconnect", (reason) => {
          console.warn(`Open socket disconnected: ${reason}`);

          // If disconnect reason is transport close or ping timeout, attempt reconnect
          if (
            reason === "transport close" ||
            reason === "ping timeout" ||
            reason === "transport error"
          ) {
            setTimeout(() => {
              if (this.openParams) {
                console.log(
                  "Attempting to reconnect open socket after disconnect..."
                );
                this.connect(this.openParams, false);
              }
            }, 3000);
          }
        });

        // Handle connection errors
        this.openSocket.on("connect_error", (err) => {
          console.error("Open WebSocket connection error:", err);
          this.isOpenConnecting = false;
          resolve(false);

          // Retry connection after a delay
          setTimeout(() => {
            if (this.openParams) {
              console.log("Retrying open socket connection after error...");
              this.connect(this.openParams, false);
            }
          }, 5000);
        });

        this.isManuallyClosed = false;
        this.socket = new WebSocket(fullUrl);

        this.socket.onopen = () => {
          console.log("✅ WebSocket connected");
          this.startPing(); // Start sending ping messages
        };

        this.socket.onclose = (event) => {
          console.warn("⚠️ WebSocket Closed:", event.reason);
          this.stopPing();

          if (!this.isManuallyClosed) {
            console.log(
              `🔄 Reconnecting in ${this.reconnectInterval / 1000} sec...`
            );
            setTimeout(
              () => this.connect(params, isProtectedRoute),
              this.reconnectInterval
            );
          }
        };
      }
    });
  }

  private startPing() {
    this.pingInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: "ping" }));
        console.log("📡 Sent WebSocket Ping");
      }
    }, 30000); // Ping every 30 sec
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Helper method to reapply all registered event handlers after reconnection
  private reapplyEventHandlers(isProtectedSocket: boolean) {
    const handlers = isProtectedSocket
      ? this.protectedEventHandlers
      : this.openEventHandlers;
    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;

    if (!socket || !socket.connected) {
      console.warn("Cannot reapply event handlers: socket not connected");
      return;
    }

    // First, remove all existing event listeners except connect/disconnect/reconnect
    const internalEvents = [
      "connect",
      "disconnect",
      "reconnect",
      "reconnect_attempt",
      "reconnect_error",
      "connect_error",
    ];

    // Apply all registered handlers
    handlers.forEach((handler) => {
      // Skip if this is an internal socket.io event
      if (internalEvents.includes(handler.event)) return;

      console.log(`Reapplying event handler for: ${handler.event}`);
      socket.off(handler.event); // Remove any existing handlers for this event
      socket.on(handler.event, handler.callback);
    });

    console.log(
      `Reapplied ${handlers.length} event handlers for ${
        isProtectedSocket ? "protected" : "open"
      } socket`
    );
  }

  // Method to disconnect the socket and cleanup event listeners
  disconnect(isProtectedSocket: boolean) {
    if (isProtectedSocket) {
      if (this.protectedSocket) {
        this.protectedSocket.removeAllListeners();
        this.protectedSocket.disconnect();
        this.protectedSocket = null;
        this.protectedParams = null;
        console.log("Protected WebSocket disconnected and cleaned up.");
      }
    } else {
      if (this.openSocket) {
        this.openSocket.removeAllListeners();
        this.openSocket.disconnect();
        this.openSocket = null;
        this.openParams = null;
        console.log("Open WebSocket disconnected and cleaned up.");
      }
    }
  }

  // Send a message to the server with acknowledgment handling
  async sendMessage(event: string, message: any, isProtectedSocket: boolean) {
    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;

    if (!socket || !socket.connected) {
      // Try to reconnect before sending
      const params = isProtectedSocket ? this.protectedParams : this.openParams;
      if (params) {
        console.log(`Reconnecting before sending message to ${event}...`);
        await this.connect(params, isProtectedSocket);
      } else {
        throw new Error("Cannot reconnect: no connection parameters saved");
      }
    }

    // Re-check connection after potential reconnect
    if (!socket || !socket.connected) {
      console.error(`Socket is not connected. Message to ${event} not sent.`);
      throw new Error("Socket is not connected.");
    }

    try {
      let ack = await socket.emitWithAck(event, message);
      return ack;
    } catch (error) {
      console.error(`Failed to send message on event ${event}:`, error);
      throw error;
    }
  }

  // Listen for events from the WebSocket
  onMessage(
    event: string,
    isProtectedSocket: boolean,
    callback: (...args: any[]) => void
  ) {
    console.log(`Registering event listener for: ${event}`);

    // Store the event handler for potential reconnection
    const handlers = isProtectedSocket
      ? this.protectedEventHandlers
      : this.openEventHandlers;

    // Check if this handler is already registered to avoid duplicates
    const isAlreadyRegistered = handlers.some(
      (handler) => handler.event === event && handler.callback === callback
    );

    if (!isAlreadyRegistered) {
      handlers.push({ event, callback });
    }

    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;

    if (socket && socket.connected) {
      console.log(`Adding listener for event: ${event}`);
      socket.on(event, callback);
    } else {
      console.warn(
        `Socket not connected. Event ${event} will be registered after connection.`
      );

      // Attempt to connect if we have parameters
      const params = isProtectedSocket ? this.protectedParams : this.openParams;
      if (params) {
        console.log(`Attempting to connect before adding event listener...`);
        this.connect(params, isProtectedSocket);
      }
    }
  }

  // Remove a specific listener for an event
  offMessage(
    event: string,
    isProtectedSocket: boolean,
    callback?: (...args: any[]) => void
  ) {
    console.log(`Removing listener for event: ${event}`);

    // Also remove from stored handlers
    const handlers = isProtectedSocket
      ? this.protectedEventHandlers
      : this.openEventHandlers;

    if (callback) {
      // Remove specific handler if callback provided
      const index = handlers.findIndex(
        (handler) => handler.event === event && handler.callback === callback
      );

      if (index !== -1) {
        handlers.splice(index, 1);
      }
    } else {
      // Remove all handlers for this event if no callback provided
      const filteredHandlers = handlers.filter(
        (handler) => handler.event !== event
      );
      if (isProtectedSocket) {
        this.protectedEventHandlers = filteredHandlers;
      } else {
        this.openEventHandlers = filteredHandlers;
      }
    }

    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;

    if (socket && socket.connected) {
      if (callback) {
        socket.off(event, callback);
      } else {
        socket.off(event);
      }
    }
  }

  // Helper to check connection status
  isConnected(isProtectedSocket: boolean): boolean {
    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;
    return !!(socket && socket.connected);
  }

  // Method to ping the server to keep connection alive
  pingServer(isProtectedSocket: boolean) {
    const socket = isProtectedSocket ? this.protectedSocket : this.openSocket;

    if (socket && socket.connected) {
      socket.emit("ping", { timestamp: Date.now() });
      return true;
    }
    return false;
  }
}

export default SocketService.getInstance(); // Export Singleton instance
